import React from 'react'
import styled, { keyframes } from 'styled-components'
import { SupportedChainId } from '../../constants/networks'
import Address from '../general/Address'
import Network from '../general/Network'
import { getEtherscanLink } from '../../utils/web3-utils'
import useRegistry from '../../hooks/useRegistry'

const Registry: React.FC = () => {
  const response = useRegistry()

  if (!response.data) return <LoaderBar />

  const chainIds = Object.values(SupportedChainId)
  const chainEntries = Object.entries(SupportedChainId)

  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <Th>Name</Th>
            {chainEntries.map(([key, value]) => (
              <Th key={key}><Network network={value} noLogo={true} /></Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.entries(response.data)
            .sort(([, itemsA]: any, [, itemsB]: any) => itemsA[0].name.localeCompare(itemsB[0].name))
            .map(([address, items]: any) =>
              items[0].name ? (
                <tr key={address}>
                  <Td>
                    <NameCell>
                      {items[0].name}
                      {items[0].stateless && <StatelessBadge>Stateless</StatelessBadge>}
                    </NameCell>
                  </Td>
                  {chainIds.map((chainId) => {
                    const chainData = items.find((i: any) => i.chainId === chainId.toString())
                    return (
                      <Td key={chainId}>
                        {chainData ? (
                          chainData.deprecated ? (
                            <DeprecatedLink
                              href={getEtherscanLink(chainId, address, 'address')}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Deprecated
                            </DeprecatedLink>
                          ) : (
                            <Address
                              address={address}
                              short
                              withLink
                              chainId={chainId}
                              type="address"
                              showIdentity={false}
                            />
                          )
                        ) : null}
                      </Td>
                    )
                  })}
                </tr>
              ) : null
            )}
        </tbody>
      </Table>
    </TableWrap>
  )
}

export default Registry

// ─── Styles ──────────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

const LoaderBar = styled.div`
  height: 2px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    display: block;
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(111, 92, 230, 0.5), transparent);
    animation: ${shimmer} 1.4s ease infinite;
  }
`

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const Th = styled.th`
  padding: 10px 12px;
  text-align: left;
  color: #a1a1a1;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`

const Td = styled.td`
  padding: 10px 12px;
  color: #a0a0a0;
  font-size: 13px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  tr:last-child & { border-bottom: none; }
`

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 12px;
  color: #ededed;
`

const StatelessBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-family: inherit;
  background: rgba(255, 204, 51, 0.08);
  color: #FFCC33;
  border: 1px solid rgba(255, 204, 51, 0.2);
`

const DeprecatedLink = styled.a`
  font-size: 11px;
  color: #ff4444;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`
